import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Utensils, Plus, Trash2, RefreshCw } from 'lucide-react';
import { mealsAPI } from '../services/api';
import { toast } from '../hooks/use-toast';

const MealLog = () => {
  const [meals, setMeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [newMeal, setNewMeal] = useState({
    meal_name: '',
    carbs: '',
    sugar: '',
    calories: '',
    notes: ''
  });

  const fetchMeals = async () => {
    setLoading(true);
    try {
      const data = await mealsAPI.getAll(100);
      setMeals(data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch meals",
        variant: "destructive"
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchMeals();
  }, []);

  const handleAddMeal = async () => {
    if (!newMeal.meal_name || !newMeal.carbs) {
      toast({
        title: "Error",
        description: "Please enter meal name and carbs",
        variant: "destructive"
      });
      return;
    }

    setSubmitting(true);
    try {
      const mealData = {
        meal_name: newMeal.meal_name,
        carbs: parseInt(newMeal.carbs),
        sugar: parseInt(newMeal.sugar) || 0,
        calories: parseInt(newMeal.calories) || 0,
        notes: newMeal.notes || null
      };

      const createdMeal = await mealsAPI.create(mealData);
      setMeals([createdMeal, ...meals]);
      setDialogOpen(false);
      setNewMeal({
        meal_name: '',
        carbs: '',
        sugar: '',
        calories: '',
        notes: ''
      });

      toast({
        title: "Success!",
        description: "Meal logged successfully"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to add meal",
        variant: "destructive"
      });
    }
    setSubmitting(false);
  };

  const handleDeleteMeal = async (mealId) => {
    try {
      await mealsAPI.delete(mealId);
      setMeals(meals.filter(meal => meal.id !== mealId));
      toast({
        title: "Deleted",
        description: "Meal removed"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete meal",
        variant: "destructive"
      });
    }
  };

  // Calculate totals for today
  const todayMeals = meals.filter(meal => {
    const mealDate = new Date(meal.time);
    const today = new Date();
    return mealDate.toDateString() === today.toDateString();
  });

  const totalCarbs = todayMeals.reduce((sum, meal) => sum + meal.carbs, 0);
  const totalSugar = todayMeals.reduce((sum, meal) => sum + meal.sugar, 0);
  const totalCalories = todayMeals.reduce((sum, meal) => sum + meal.calories, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Meal Log</h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Track your daily nutrition</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchMeals} className="dark:border-gray-600 dark:text-gray-300">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-emerald-500 to-blue-500 hover:from-emerald-600 hover:to-blue-600 shadow-lg hover:shadow-xl transition-all">
                <Plus className="w-4 h-4 mr-2" />
                Log Meal
              </Button>
            </DialogTrigger>
            <DialogContent className="dark:bg-gray-800">
              <DialogHeader>
                <DialogTitle className="dark:text-white">Log a Meal</DialogTitle>
                <DialogDescription className="dark:text-gray-400">
                  Add details about what you ate
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label htmlFor="meal_name" className="dark:text-gray-300">Meal Name</Label>
                  <Input
                    id="meal_name"
                    type="text"
                    placeholder="e.g., Oatmeal with Berries"
                    value={newMeal.meal_name}
                    onChange={(e) => setNewMeal({ ...newMeal, meal_name: e.target.value })}
                    className="dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="carbs" className="dark:text-gray-300">Carbs (g)</Label>
                    <Input
                      id="carbs"
                      type="number"
                      placeholder="45"
                      value={newMeal.carbs}
                      onChange={(e) => setNewMeal({ ...newMeal, carbs: e.target.value })}
                      className="dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="sugar" className="dark:text-gray-300">Sugar (g)</Label>
                    <Input
                      id="sugar"
                      type="number"
                      placeholder="12"
                      value={newMeal.sugar}
                      onChange={(e) => setNewMeal({ ...newMeal, sugar: e.target.value })}
                      className="dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="calories" className="dark:text-gray-300">Calories</Label>
                    <Input
                      id="calories"
                      type="number"
                      placeholder="320"
                      value={newMeal.calories}
                      onChange={(e) => setNewMeal({ ...newMeal, calories: e.target.value })}
                      className="dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="notes" className="dark:text-gray-300">Notes (optional)</Label>
                  <Input
                    id="notes"
                    type="text"
                    placeholder="Any additional notes"
                    value={newMeal.notes}
                    onChange={(e) => setNewMeal({ ...newMeal, notes: e.target.value })}
                    className="dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>
                <Button 
                  onClick={handleAddMeal} 
                  className="w-full bg-gradient-to-r from-emerald-500 to-blue-500"
                  disabled={submitting}
                >
                  {submitting ? 'Saving...' : 'Save Meal'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Today's Summary */}
      <div className="grid md:grid-cols-4 gap-6">
        <Card className="shadow-md border-0 dark:bg-gray-800">
          <CardContent className="p-6">
            <p className="text-sm text-gray-500 dark:text-gray-400">Today's Meals</p>
            <p className="text-3xl font-bold text-gray-900 dark:text-white">{todayMeals.length}</p>
          </CardContent>
        </Card>
        <Card className="shadow-md border-0 dark:bg-gray-800">
          <CardContent className="p-6">
            <p className="text-sm text-gray-500 dark:text-gray-400">Total Carbs</p>
            <p className="text-3xl font-bold text-emerald-600 dark:text-emerald-400">{totalCarbs}g</p>
          </CardContent>
        </Card>
        <Card className="shadow-md border-0 dark:bg-gray-800">
          <CardContent className="p-6">
            <p className="text-sm text-gray-500 dark:text-gray-400">Total Sugar</p>
            <p className="text-3xl font-bold text-orange-600 dark:text-orange-400">{totalSugar}g</p>
          </CardContent>
        </Card>
        <Card className="shadow-md border-0 dark:bg-gray-800">
          <CardContent className="p-6">
            <p className="text-sm text-gray-500 dark:text-gray-400">Total Calories</p>
            <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">{totalCalories}</p>
          </CardContent>
        </Card>
      </div>

      {/* Meals List */}
      <Card className="shadow-md border-0 dark:bg-gray-800">
        <CardHeader>
          <CardTitle className="dark:text-white">Meal History</CardTitle>
          <CardDescription className="dark:text-gray-400">Your logged meals</CardDescription>
        </CardHeader>
        <CardContent>
          {meals.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <Utensils className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg">No meals logged yet</p>
              <p className="text-sm mt-1">Click "Log Meal" to add your first entry</p>
            </div>
          ) : (
            <div className="space-y-3">
              {meals.map((meal) => (
                <div 
                  key={meal.id} 
                  className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-emerald-100 to-blue-100 dark:from-emerald-900/50 dark:to-blue-900/50 rounded-lg flex items-center justify-center">
                      <Utensils className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-white">{meal.meal_name}</p>
                      <div className="flex gap-4 text-sm text-gray-500 dark:text-gray-400">
                        <span>{meal.carbs}g carbs</span>
                        <span>{meal.sugar}g sugar</span>
                        <span>{meal.calories} cal</span>
                      </div>
                      {meal.notes && <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{meal.notes}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {new Date(meal.time).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {new Date(meal.time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                      </p>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => handleDeleteMeal(meal.id)}
                      className="text-gray-400 hover:text-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MealLog;
